/**
 * Fountain FX (P1-T08): three pooled particle systems assigned to the three
 * fountains nearest the camera (prototype recipe: jet velocity from the
 * target height, spread from basin radius) plus a pool caustic disc in each
 * basin. Fountains come from the streamed cell instance lists, so no extra
 * manifest data is needed.
 */
import {
  Color3,
  Color4,
  DynamicTexture,
  Mesh,
  MeshBuilder,
  ParticleSystem,
  Scene,
  StandardMaterial,
  Vector3,
} from 'babylonjs';

const NEAR_MAX = 3;
const REACH_SQ = 220 * 220;
/** jet target height (m) -> launch speed, and basin ring radius (m) */
const JET_HEIGHT = 5;
const BASIN_R = 6.4;
const BASIN_WATER_Y = 0.85;

function flakeTexture(scene: Scene): DynamicTexture {
  const tex = new DynamicTexture('kn_flake', { width: 64, height: 64 }, scene, false);
  const g = tex.getContext();
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.4, 'rgba(255,255,255,0.5)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}

function poolMaterial(scene: Scene): StandardMaterial {
  const m = new StandardMaterial('fountainPool', scene);
  m.diffuseColor = new Color3(0.06, 0.2, 0.26);
  m.specularColor = new Color3(0.5, 0.6, 0.65);
  m.specularPower = 96;
  m.emissiveColor = new Color3(0.01, 0.05, 0.07);
  return m;
}

export class FountainFx {
  private readonly byCell = new Map<string, Vector3[]>();
  private readonly pools: ParticleSystem[] = [];
  private readonly poolDiscs: Mesh[] = [];
  private readonly poolMat: StandardMaterial;
  private lastFocus = new Vector3(1e9, 0, 1e9);
  private nextAssign = 0;

  constructor(scene: Scene) {
    this.poolMat = poolMaterial(scene);
    const flake = flakeTexture(scene);
    for (let i = 0; i < NEAR_MAX; i++) {
      const ps = new ParticleSystem(`fountain${String(i)}`, 900, scene);
      ps.particleTexture = flake;
      ps.emitter = new Vector3(0, -100, 0);
      ps.minEmitBox = new Vector3(-0.2, 0, -0.2);
      ps.maxEmitBox = new Vector3(0.2, 0, 0.2);
      ps.direction1 = new Vector3(-0.35, 1, -0.35);
      ps.direction2 = new Vector3(0.35, 1, 0.35);
      ps.minEmitPower = 8;
      ps.maxEmitPower = 10.5;
      ps.gravity = new Vector3(0, -9.8, 0);
      ps.minLifeTime = 1.4;
      ps.maxLifeTime = 2.0;
      ps.minSize = 0.12;
      ps.maxSize = 0.3;
      ps.emitRate = 0;
      ps.color1 = new Color4(0.85, 0.93, 1, 0.55);
      ps.color2 = new Color4(0.7, 0.85, 1, 0.4);
      ps.colorDead = new Color4(0.7, 0.85, 1, 0);
      ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
      ps.start();
      this.pools.push(ps);

      const disc = MeshBuilder.CreateDisc(
        `fountainPool${String(i)}`,
        { radius: BASIN_R, tessellation: 28 },
        scene,
      );
      disc.rotation.x = Math.PI / 2;
      disc.material = this.poolMat;
      disc.isPickable = false;
      disc.setEnabled(false);
      this.poolDiscs.push(disc);
    }
  }

  setCell(cellId: string, list: { type: string; m: number[] }[]): void {
    const pts = list
      .filter((x) => x.type === 'fountain')
      .map((x) => new Vector3(x.m[12] ?? 0, 0, x.m[14] ?? 0));
    if (pts.length > 0) this.byCell.set(cellId, pts);
    else this.byCell.delete(cellId);
  }

  /** Freeze particle motion for still-frame checks (engine dt otherwise
   *  keeps particles alive while the sim clock is frozen). */
  setFrozen(v: boolean): void {
    for (const ps of this.pools) ps.updateSpeed = v ? 0 : 1;
  }

  removeCell(cellId: string): void {
    this.byCell.delete(cellId);
  }

  /** Assign the three pools to the nearest fountains; re-checks every 250 ms. */
  update(camPos: Vector3, nowMs: number): void {
    if (nowMs < this.nextAssign) return;
    this.nextAssign = nowMs + 250;
    if (Math.abs(camPos.x - this.lastFocus.x) < 0.5 && Math.abs(camPos.z - this.lastFocus.z) < 0.5) return;
    this.lastFocus.copyFrom(camPos);

    const all: { d2: number; p: Vector3 }[] = [];
    for (const pts of this.byCell.values())
      for (const p of pts) {
        const dx = p.x - camPos.x;
        const dz = p.z - camPos.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < REACH_SQ) all.push({ d2, p });
      }
    all.sort((a, b) => a.d2 - b.d2);

    const speed = Math.sqrt(2 * 9.8 * JET_HEIGHT);
    const spread = Math.min(0.45, BASIN_R * 0.035);
    for (let i = 0; i < NEAR_MAX; i++) {
      const ps = this.pools[i];
      const disc = this.poolDiscs[i];
      if (!ps || !disc) continue;
      const hit = all[i];
      if (!hit) {
        ps.emitRate = 0;
        (ps.emitter as Vector3).set(0, -100, 0);
        disc.setEnabled(false);
        continue;
      }
      const p = hit.p;
      (ps.emitter as Vector3).set(p.x, BASIN_WATER_Y + 0.35, p.z);
      ps.emitRate = 8 * 40;
      ps.minEmitPower = speed * 0.85;
      ps.maxEmitPower = speed;
      ps.direction1.set(-spread, 1, -spread);
      ps.direction2.set(spread, 1, spread);
      disc.position.set(p.x, BASIN_WATER_Y, p.z);
      disc.setEnabled(true);
    }
  }

  /** night factor dims the pool sheen (called each frame, cheap) */
  setNight(night: number): void {
    const k = 1 - night * 0.65;
    this.poolMat.emissiveColor.set(0.01 * k, 0.05 * k, 0.07 * k);
  }

  dispose(): void {
    for (const p of this.pools) p.dispose();
    for (const d of this.poolDiscs) d.dispose();
    this.poolMat.dispose();
  }
}
